// SOURCE-OF-TRUTH LOCK: Approved default home. Do not replace with card/profile/chat/service page.
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientApi } from '../api';
import CriticalAlerts from '../components/CriticalAlerts';
import type { DashboardSummary, MarketplaceProvider, MedicineReminder } from '../types';
import Spinner from '../../../components/ui/Spinner';
import { NHCAuditTimeline, NHCLogo, T } from '@design/index';
import ApprovedPatientCard from '../components/ApprovedPatientCard';
import { loadPatientIdentity } from '../utils/patientIdentity';
import type { PatientIdentity } from '../utils/patientIdentity';

const IMAGING_ACCENT = '#4F46E5';

const PROVIDER_TYPE_META: Record<string, { label: string; color: string; btn: string; route: string }> = {
  DOCTOR: { label: 'Doctor', color: T.navy, btn: 'Book', route: '/patient/care-services/doctors' },
  LAB: { label: 'Lab', color: '#7C3AED', btn: 'Book Test', route: '/patient/care-services/labs' },
  PHARMACY: { label: 'Pharmacy', color: '#059669', btn: 'Order Now', route: '/patient/care-services/pharmacy' },
  HOSPITAL: { label: 'Hospital', color: T.danger, btn: 'Book OPD', route: '/patient/care-services/hospitals' },
  IMAGING: { label: 'Imaging', color: IMAGING_ACCENT, btn: 'Book Scan', route: '/patient/care-services/imaging' },
  AMBULANCE: { label: 'Ambulance', color: '#EA580C', btn: 'Request', route: '/patient/care-services/ambulance' },
};

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatTime(d: string) {
  return new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

const card = (extra?: React.CSSProperties): React.CSSProperties => ({
  background: T.white,
  border: `1px solid ${T.border}`,
  borderRadius: 16,
  padding: 14,
  boxShadow: T.shadow,
  ...extra,
});

const pill = (text: string, color: string): React.CSSProperties => ({
  fontSize: 9,
  fontWeight: 600,
  color,
  background: `${color}12`,
  padding: '3px 10px',
  borderRadius: 6,
  fontFamily: T.font,
  border: `1px solid ${color}25`,
});

const actionBtn = (bg: string, color: string, outline = false): React.CSSProperties => ({
  fontSize: 10,
  fontWeight: 600,
  color: outline ? bg : color,
  background: outline ? 'transparent' : bg,
  border: outline ? `1.5px solid ${bg}` : 'none',
  borderRadius: 8,
  padding: '7px 14px',
  cursor: 'pointer',
  fontFamily: T.font,
  whiteSpace: 'nowrap',
});

const AlertDot = ({ c }: { c: string }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z M12 9v4 M12 17h.01" /></svg>
);

function useIsDesktop(bp = 1024) {
  const [d, setD] = useState(window.innerWidth >= bp);
  useEffect(() => { const h = () => setD(window.innerWidth >= bp); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, [bp]);
  return d;
}

export default function PatientHome() {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllServices, setShowAllServices] = useState(false);
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  const [pid, setPid] = useState<PatientIdentity | null>(null);
  const [alerts, setAlerts] = useState<{ id: string; title: string; body: string | null; category: string; read: boolean; createdAt: string }[]>([]);
  const [nearbyProviders, setNearbyProviders] = useState<MarketplaceProvider[]>([]);
  const [markingDose, setMarkingDose] = useState(false);
  const [doseNote, setDoseNote] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    // Each feed fails independently — one endpoint being down must never
    // blank the others.
    patientApi.getDashboard()
      .then(d => { if (alive) setData(d); })
      .catch(() => { if (alive) setError('Could not load your dashboard from the server. Showing locally saved data only.'); })
      .finally(() => { if (alive) setLoading(false); });
    loadPatientIdentity().then(({ identity }) => { if (alive) setPid(identity); });
    patientApi.getNotifications()
      .then(n => { if (alive) setAlerts(n.filter(x => !x.read).slice(0, 5)); })
      .catch(() => {});
    patientApi.getMarketplaceProviders({ verified: 'true', limit: 4 })
      .then(r => { if (alive) setNearbyProviders(r.providers ?? []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Re-read the dashboard after a dose is marked, so the card renders from
  // the server's record rather than an optimistic guess of our own.
  const refreshDashboard = () => { patientApi.getDashboard().then(setData).catch(() => {}); };

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;

  const upcomingAppointments = data?.upcomingAppointments || [];
  const activeReminders = data?.activeReminders || [];
  const recentTimeline = data?.recentTimeline || [];

  const firstAppt = upcomingAppointments[0];
  const firstMed = activeReminders[0];

  // ── Today's doses, as the SERVER has them ──
  // `/home/summary` carries each active reminder's logs for today, each keyed
  // by the scheduled slot it answers. This card could name a medicine but not
  // say whether today's dose had been taken, which is the one thing a dose
  // reminder is for.
  const nowHHMM = `${String(new Date().getHours()).padStart(2, '0')}:${String(new Date().getMinutes()).padStart(2, '0')}`;
  const doseState = (m: MedicineReminder, time: string): 'TAKEN' | 'SKIPPED' | 'MISSED' | 'DUE' => {
    const log = m.adherenceLogs.find(l => l.scheduledTime === time);
    if (log) return log.taken ? 'TAKEN' : 'SKIPPED';
    return time < nowHHMM ? 'MISSED' : 'DUE';
  };
  const DOSE_COLOR: Record<string, string> = { TAKEN: T.success, SKIPPED: T.textTer, MISSED: T.danger, DUE: T.warning };
  let dosesToday = 0, dosesTaken = 0;
  for (const m of activeReminders) {
    for (const t of m.times) {
      dosesToday++;
      if (doseState(m, t) === 'TAKEN') dosesTaken++;
    }
  }
  /** The slot the Taken/Skip buttons act on — the first one still unanswered. */
  const openSlot = firstMed?.times.find(t => { const s = doseState(firstMed, t); return s === 'DUE' || s === 'MISSED'; }) ?? null;

  async function markDose(taken: boolean) {
    if (!firstMed || !openSlot || markingDose) return;
    setMarkingDose(true);
    setDoseNote(null);
    try {
      await patientApi.logAdherence(firstMed.id, { taken, scheduledTime: openSlot, source: 'HOME' });
      refreshDashboard();
    } catch {
      // Never report a dose the record did not accept.
      setDoseNote('Could not record that dose. Please try again.');
    } finally {
      setMarkingDose(false);
    }
  }

  const AUDIT_ENTRIES = recentTimeline.slice(0, 4).map(event => ({
    text: event.title + (event.description ? ` — ${event.description}` : ''),
    time: formatDate(event.eventDate),
    color: T.teal,
  }));

  // ── Desktop sizing helpers ──
  const D = isDesktop;
  const dCard = (extra?: React.CSSProperties): React.CSSProperties => card({
    padding: D ? 20 : 14,
    borderRadius: D ? 16 : 16,
    ...extra,
  });
  const dPill = (text: string, color: string): React.CSSProperties => ({
    ...pill(text, color),
    fontSize: D ? 11 : 9,
    padding: D ? '4px 12px' : '3px 10px',
  });
  const dActionBtn = (bg: string, color: string, outline = false): React.CSSProperties => ({
    ...actionBtn(bg, color, outline),
    fontSize: D ? 13 : 10,
    padding: D ? '10px 20px' : '7px 14px',
    borderRadius: D ? 10 : 8,
  });
  const sectionTitle = (label: string) => (
    <span style={{ fontSize: D ? 18 : 12, fontWeight: 700, color: T.text, fontFamily: T.font }}>{label}</span>
  );
  const sectionSub = (label: string) => (
    <div style={{ fontSize: D ? 13 : 9, color: T.textSec, fontFamily: T.font, marginTop: D ? 2 : 0 }}>{label}</div>
  );
  const iconBox = (color: string, size?: number) => ({
    width: D ? (size || 44) : (size ? size - 8 : 28),
    height: D ? (size || 44) : (size ? size - 8 : 28),
    borderRadius: D ? 12 : 8,
    background: `${color}12`,
    display: 'grid' as const,
    placeItems: 'center' as const,
    flexShrink: 0 as const,
  });

  return (
    <div style={{ padding: D ? '24px 32px 100px' : '0 16px 16px', display: 'flex', flexDirection: 'column', gap: D ? 28 : 14, maxWidth: D ? 1280 : '100%', margin: '0 auto', width: '100%', fontFamily: T.font, boxSizing: 'border-box' as const, overflowX: 'hidden' as const, minWidth: 0 }}>

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '8px 12px' }}>
          {error}
        </div>
      )}

      {/* Findings a provider flagged as urgent — first thing on the screen */}
      <CriticalAlerts />

      {/* ── A0. CARE SEARCH + QUICK CONTROLS ── */}
      {!D && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 20,
          background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
          margin: '0 -16px 0', padding: '8px 16px 8px',
          borderBottom: `1px solid ${T.border}`,
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          <div onClick={() => navigate('/patient/search')} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 6px 6px 14px', background: T.ice, borderRadius: 14, border: `1px solid ${T.border}`, cursor: 'pointer' }}>
            <svg width={17} height={17} viewBox="0 0 24 24" fill="none" stroke={T.textTer} strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.35-4.35" /></svg>
            <span style={{ fontSize: 13, color: T.textTer, fontFamily: T.font, flex: 1 }}>Search care, records, medicines…</span>
            <button onClick={e => { e.stopPropagation(); navigate('/patient/scan'); }} style={{ width: 34, height: 34, borderRadius: 10, background: T.navy, border: 'none', display: 'grid', placeItems: 'center', cursor: 'pointer', flexShrink: 0 }}>
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#FFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="3" height="3" /><path d="M14 21h3 M21 14v3 M21 21h.01" /></svg>
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            {[
              { label: 'Nearby', color: T.teal, icon: 'M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z M12 7.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z', route: '/patient/care-services' },
              { label: 'Scan QR', color: T.navy, icon: 'M3 3h7v7H3z M14 3h7v7h-7z M3 14h7v7H3z M14 14h3v3h-3z M14 21h3 M21 14v3 M21 21h.01', route: '/patient/scan' },
              { label: 'Emergency', color: T.danger, icon: 'M12 9v4 M12 17h.01 M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z', route: '/patient/emergency' },
            ].map((c) => (
              <button key={c.label} onClick={() => navigate(c.route)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: c.color, background: `${c.color}0E`, border: `1px solid ${c.color}22`, borderRadius: 12, padding: '10px 8px', cursor: 'pointer', fontFamily: T.font, whiteSpace: 'nowrap' }}>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={c.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {c.icon.split(' M').map((seg, i) => <path key={i} d={i === 0 ? seg : `M${seg}`} />)}
                </svg>
                {c.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── PREMIUM IDENTITY RIBBON (Desktop — Task 2) ── */}
      {D && pid && (
        <div style={{
          background: 'linear-gradient(135deg, #FAFFFE 0%, #F0FDFA 50%, #F8FFFE 100%)',
          border: `1.5px solid ${T.teal}20`,
          borderRadius: 18,
          padding: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          position: 'relative' as const,
          overflow: 'hidden',
          boxShadow: `0 4px 24px ${T.teal}08, 0 1px 3px ${T.teal}06`,
        }}>
          {/* Logo watermark */}
          <div style={{ position: 'absolute', right: -28, bottom: -18, opacity: 0.045, pointerEvents: 'none', zIndex: 0 }}>
            <NHCLogo w={190} variant="navy" />
          </div>
          {/* LEFT: Patient photo/avatar with NHC mark badge + name + VERIFIED + NHC ID */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
              {/* Patient avatar with NHC mark badge */}
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%',
                  background: `linear-gradient(135deg, ${T.teal}20, ${T.navy}15)`,
                  border: `2.5px solid ${T.teal}40`,
                  display: 'grid', placeItems: 'center',
                }}>
                  <span style={{ fontSize: 22, fontWeight: 800, color: T.teal, fontFamily: T.font }}>{pid.patientName?.charAt(0) || 'P'}</span>
                </div>
                {/* NHC mark badge */}
                <div style={{
                  position: 'absolute', bottom: -2, right: -2,
                  width: 22, height: 22, borderRadius: '50%',
                  background: '#fff', border: `1.5px solid ${T.teal}40`,
                  display: 'grid', placeItems: 'center',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                }}>
                  <NHCLogo w={14} variant="color" />
                </div>
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 20, fontWeight: 800, color: T.text, fontFamily: T.font, lineHeight: 1.2 }}>{pid.patientName}</span>
                  {pid.verifiedStatus && (
                    <span style={{ fontSize: 9, fontWeight: 700, color: '#FFF', background: T.success, padding: '3px 10px', borderRadius: 6, whiteSpace: 'nowrap', fontFamily: T.font }}>VERIFIED</span>
                  )}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.teal, fontFamily: T.mono, marginTop: 3, letterSpacing: '0.03em' }}>
                  {pid.nhcIdDisplay}
                </div>
              </div>
            </div>
            {/* RIGHT: QR Ready + NFC + NeoVerify shield */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
              <div onClick={() => navigate('/patient/nhc-card')} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 16px', borderRadius: 14,
                background: T.white, border: `1px solid ${T.border}`,
                cursor: 'pointer', boxShadow: T.shadow,
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.navy} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="3" height="3" />
                </svg>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: T.navy, fontFamily: T.font }}>QR Ready</div>
                  <div style={{ fontSize: 9, color: T.textTer, fontFamily: T.font }}>NFC Enabled</div>
                </div>
              </div>
              <div onClick={() => navigate('/patient/nhc-card')} style={{
                width: 44, height: 44, borderRadius: 12,
                background: `${T.navy}08`, border: `1px solid ${T.navy}15`,
                display: 'grid', placeItems: 'center', cursor: 'pointer',
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.navy} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
              </div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
            {[
              { label: 'Upcoming Appointments', value: data?.stats.upcomingAppointmentCount || '—', color: T.teal, icon: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z', route: '/patient/appointments' },
              { label: 'Active Medicines', value: data?.stats.activeMedicineCount || '—', color: T.navy, icon: 'M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5 M10.5 1.5V3h3V1.5', route: '/patient/medicines' },
              { label: 'Pending Lab Orders', value: data?.stats.pendingLabCount || '—', color: '#7C3AED', icon: 'M22 12h-4l-3 9L9 3l-3 9H2', route: '/patient/lab-reports' },
              { label: 'Insurance Policies', value: data?.stats.activePolicyCount || '—', color: T.warning, icon: 'M12 1L2 5v8c0 7 4.3 11 10 13 5.7-2 10-6 10-13V5L12 1z M9 12l2 2 4-4', route: '/patient/insurance' },
            ].map(s => (
              <div key={s.label} onClick={() => navigate(s.route)} style={{
                background: T.white, border: `1px solid ${T.border}`, borderRadius: 14,
                padding: 16, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 8,
                boxShadow: T.shadow,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: `${s.color}10`, display: 'grid', placeItems: 'center' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={s.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      {s.icon.split(' M').map((seg, i) => <path key={i} d={i === 0 ? seg : `M${seg}`} />)}
                    </svg>
                  </div>
                  <span style={{ fontSize: 22, fontWeight: 800, color: T.text, fontFamily: T.font }}>{s.value}</span>
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, color: T.textSec, fontFamily: T.font }}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── TODAY'S CARE ── */}
      {D && <div style={{ marginBottom: -12 }}>{sectionTitle("Today's Care")}{sectionSub('Emergency, appointments, and medication reminders')}</div>}

      {/* ── A1. EMERGENCY PANEL ── */}
      <div style={dCard({ padding: 0, overflow: 'hidden', display: 'flex' })}>
        <div style={{ width: D ? 6 : 4, background: T.danger, flexShrink: 0 }} />
        <div style={{ flex: 1, padding: D ? 20 : 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: D ? 14 : 10, marginBottom: D ? 16 : 10 }}>
            <div style={iconBox(T.danger, 48)}>
              <svg width={D ? 22 : 16} height={D ? 22 : 16} viewBox="0 0 24 24" fill="none" stroke={T.danger} strokeWidth="2" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72" /></svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: D ? 18 : 13, fontWeight: 700, color: T.text, fontFamily: T.font, lineHeight: 1.1 }}>Emergency</div>
              <div style={{ fontSize: D ? 13 : 9.5, color: T.textSec, fontFamily: T.font, marginTop: D ? 4 : 1 }}>Call emergency support or book ambulance</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: D ? '1fr 1.5fr 1.5fr' : '1fr 1fr 1fr', gap: D ? 12 : 5 }}>
            <button onClick={() => navigate('/patient/emergency')} style={{ ...dActionBtn(T.danger, '#FFF'), textAlign: 'center' as const, padding: D ? '12px 0' : '7px 0', fontSize: D ? 13 : 9, whiteSpace: 'nowrap' as const, overflow: 'hidden' as const }}>SOS</button>
            <button onClick={() => navigate('/patient/emergency')} style={{ ...dActionBtn(T.danger, T.danger, true), textAlign: 'center' as const, padding: D ? '12px 0' : '7px 0', fontSize: D ? 13 : 8.5, whiteSpace: 'nowrap' as const, overflow: 'hidden' as const }}>{D ? 'Book Ambulance' : 'Ambulance'}</button>
            <button onClick={() => navigate('/patient/emergency')} style={{ ...dActionBtn(T.danger, T.danger, true), textAlign: 'center' as const, padding: D ? '12px 0' : '7px 0', fontSize: D ? 13 : 8.5, whiteSpace: 'nowrap' as const, overflow: 'hidden' as const }}>{D ? 'Share Location' : 'Share'}</button>
          </div>
          <div style={{ fontSize: D ? 11 : 8, color: T.textTer, fontFamily: T.font, marginTop: D ? 12 : 8 }}>Emergency access is audit logged</div>
        </div>
      </div>

      {/* ── A2+A3. Upcoming Appointment + Current Medicine ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: D ? 20 : 10 }}>
        {/* A2. Upcoming Appointment */}
        <div onClick={() => navigate('/patient/appointments')} style={dCard({ display: 'flex', flexDirection: 'column', gap: D ? 8 : 4, cursor: 'pointer', minHeight: D ? 160 : undefined })}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={iconBox(T.navy, 44)}>
              <svg width={D ? 18 : 14} height={D ? 18 : 14} viewBox="0 0 24 24" fill="none" stroke={T.navy} strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4 M8 2v4 M3 10h18" /></svg>
            </div>
            {firstAppt && (
              <span style={{ ...dPill(firstAppt.consultationType === 'VIDEO' ? 'Video' : 'OPD', T.navy) }}>
                {firstAppt.consultationType === 'VIDEO' ? 'VIDEO' : 'OPD'}
              </span>
            )}
          </div>
          {firstAppt ? (
            <>
              <div style={{ fontSize: D ? 15 : 11, fontWeight: 700, color: T.text, fontFamily: T.font, marginTop: D ? 6 : 3 }}>{firstAppt.doctorName}</div>
              {/* The doctor's speciality rides on the appointment record and
                  was being dropped; the hospital is not always set. */}
              {[firstAppt.hospitalName, firstAppt.speciality].filter(Boolean).length > 0 && (
                <div style={{ fontSize: D ? 13 : 9, color: T.textSec, fontFamily: T.font }}>
                  {[firstAppt.hospitalName, firstAppt.speciality].filter(Boolean).join(' · ')}
                </div>
              )}
              <div style={{ fontSize: D ? 14 : 9.5, fontWeight: 600, color: T.text, fontFamily: T.font, marginTop: D ? 2 : 1 }}>{formatDate(firstAppt.appointedAt)} · {formatTime(firstAppt.appointedAt)}</div>
              <span style={{ ...dPill(firstAppt.status, firstAppt.status === 'CONFIRMED' ? T.success : T.info), alignSelf: 'flex-start', marginTop: D ? 4 : 2 }}>{firstAppt.status}</span>
              {/* This card shows the next one; the server counts them all. */}
              {(data?.stats.upcomingAppointmentCount ?? 0) > 1 && (
                <div style={{ fontSize: D ? 13 : 9, color: T.textSec, fontFamily: T.font, marginTop: D ? 4 : 2 }}>
                  +{(data?.stats.upcomingAppointmentCount ?? 1) - 1} more upcoming
                </div>
              )}
            </>
          ) : (
            <div style={{ fontSize: D ? 14 : 10, color: T.textTer, fontFamily: T.font, marginTop: D ? 8 : 3 }}>No upcoming appointments</div>
          )}
          <div style={{ display: 'flex', gap: D ? 10 : 5, marginTop: 'auto', paddingTop: D ? 12 : 8 }}>
            <button style={dActionBtn(T.teal, '#FFF')}>Check-in</button>
            <button style={dActionBtn(T.navy, '#FFF')}>View</button>
          </div>
        </div>

        {/* A3. Current Medicine Dose */}
        <div onClick={() => navigate('/patient/medicines')} style={dCard({ display: 'flex', flexDirection: 'column', gap: D ? 8 : 4, cursor: 'pointer', minHeight: D ? 160 : undefined })}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={iconBox(T.teal, 44)}>
              <svg width={D ? 18 : 14} height={D ? 18 : 14} viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth="2" strokeLinecap="round"><path d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5 M10.5 1.5V3h3V1.5 M12 12v3" /></svg>
            </div>
            {/* Every scheduled time today, coloured by what the server has
                logged against it — not just the first one. */}
            {firstMed && firstMed.times.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: D ? 6 : 4, justifyContent: 'flex-end' }}>
                {firstMed.times.map(t => (
                  <span key={t} style={{ ...dPill(t, DOSE_COLOR[doseState(firstMed, t)]) }}>{t}</span>
                ))}
              </div>
            )}
          </div>
          {firstMed ? (
            <>
              <div style={{ fontSize: D ? 15 : 11, fontWeight: 700, color: T.text, fontFamily: T.font, marginTop: D ? 6 : 3 }}>{firstMed.medicineName}</div>
              <div style={{ fontSize: D ? 13 : 9, color: T.textSec, fontFamily: T.font }}>{[firstMed.dosage, firstMed.frequency.replace(/_/g, ' ').toLowerCase()].filter(Boolean).join(' · ')}</div>
              {/* The dose tally and the full active-medicine count the server
                  reports — the card used to show one medicine and imply it was
                  the whole picture. */}
              <div style={{ fontSize: D ? 13 : 9, color: T.textSec, fontFamily: T.font, marginTop: D ? 4 : 2 }}>
                {dosesToday > 0 ? `${dosesTaken}/${dosesToday} doses taken today` : 'No doses scheduled today'}
                {data?.stats.activeMedicineCount ? ` · ${data.stats.activeMedicineCount} active` : ''}
              </div>
              {doseNote && (
                <div style={{ fontSize: D ? 12 : 8.5, color: T.warning, fontFamily: T.font, marginTop: 2 }}>{doseNote}</div>
              )}
            </>
          ) : (
            <div style={{ fontSize: D ? 14 : 10, color: T.textTer, fontFamily: T.font, marginTop: D ? 8 : 3 }}>No active medicines</div>
          )}
          <div style={{ display: 'flex', gap: D ? 10 : 5, marginTop: 'auto', paddingTop: D ? 12 : 8 }}>
            <button
              disabled={!openSlot || markingDose}
              onClick={e => { e.stopPropagation(); void markDose(true); }}
              style={{ ...dActionBtn(T.success, '#FFF'), opacity: !openSlot || markingDose ? 0.5 : 1 }}
            >
              Taken
            </button>
            <button
              disabled={!openSlot || markingDose}
              onClick={e => { e.stopPropagation(); void markDose(false); }}
              style={{ ...dActionBtn(T.textTer, T.textSec, true), opacity: !openSlot || markingDose ? 0.5 : 1 }}
            >
              Skip
            </button>
          </div>
        </div>
      </div>

      {/* ── QUICK ACTIONS (Desktop — 6 cards) ── */}
      {D && (
        <div>
          <div style={{ marginBottom: 16 }}>{sectionTitle('Quick Actions')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
            {[
              { title: 'Book Appointment', subtitle: 'Find verified doctors', color: T.navy, icon: 'M12 11a4 4 0 100-8 4 4 0 000 8z M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2 M18 8v4 M16 10h4', route: '/patient/care-services/doctors' },
              { title: 'Book Lab Test', subtitle: 'NABL labs, home collection', color: T.teal, icon: 'M9 3h6 M10 3v6.5L5.5 18a2 2 0 001.8 3h9.4a2 2 0 001.8-3L14 9.5V3', route: '/patient/care-services/labs' },
              { title: 'Order Medicines', subtitle: 'Pharmacy delivery & eRx', color: '#059669', icon: 'M10.5 20.5 4 14a4.95 4.95 0 017-7l6.5 6.5a4.95 4.95 0 01-7 7Z M8.5 8.5l7 7', route: '/patient/care-services/pharmacy' },
              { title: 'Open NHC Card', subtitle: 'NeoHealthCard Digital ID', color: T.teal, icon: 'M3 3h7v7H3z M14 3h7v7h-7z M3 14h7v7H3z M14 14h3v3h-3z M14 21h3 M21 14v3 M21 21h.01', route: '/patient/nhc-card' },
              { title: 'Share Records', subtitle: 'Consent-based sharing', color: T.success, icon: 'M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8 M16 6l-4-4-4 4 M12 2v13', route: '/patient/share-center' },
              { title: 'Insurance', subtitle: 'Coverage & claims', color: T.warning, icon: 'M12 1L2 5v8c0 7 4.3 11 10 13 5.7-2 10-6 10-13V5L12 1z M9 12l2 2 4-4', route: '/patient/insurance' },
            ].map(a => (
              <div key={a.title} onClick={() => navigate(a.route)} style={{
                background: T.white, border: `1px solid ${T.border}`, borderRadius: 14,
                padding: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12,
                boxShadow: T.shadow, transition: 'box-shadow 0.15s',
              }}>
                <div style={{ width: 42, height: 42, borderRadius: 11, background: `${a.color}0C`, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke={a.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {a.icon.split(' M').map((seg, i) => <path key={i} d={i === 0 ? seg : `M${seg}`} />)}
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: T.font, lineHeight: 1.2 }}>{a.title}</div>
                  <div style={{ fontSize: 11, color: T.textSec, fontFamily: T.font, marginTop: 2 }}>{a.subtitle}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── RECORDS & TRUST ── */}
      {D && <div style={{ marginBottom: -12 }}>{sectionTitle('Records & Trust')}{sectionSub('Identity verification, digital health card, and secure records')}</div>}

      {/* ── NEOVERIFY IDENTITY ── */}
      {D && (
        <div style={{
          background: `${T.navy}05`, border: `1.5px solid ${T.navy}15`, borderRadius: 16,
          padding: 20, display: 'flex', alignItems: 'center', gap: 20,
        }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14,
            background: `${T.navy}10`, display: 'grid', placeItems: 'center', flexShrink: 0,
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={T.navy} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <path d="M9 12l2 2 4-4" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.font }}>NeoVerify Identity</div>
            <div style={{ fontSize: 13, color: T.textSec, fontFamily: T.font, marginTop: 2 }}>QR / NFC / Consent-based verification at any NeoHealthCard facility</div>
            <div style={{ fontSize: 11, color: T.textTer, fontFamily: T.font, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 5, height: 5, borderRadius: 3, background: T.success }} />
              Every access is logged and auditable
            </div>
          </div>
          <button onClick={() => navigate('/patient/nhc-card')} style={{
            background: T.navy, color: '#FFF', border: 'none', borderRadius: 12,
            padding: '12px 24px', fontSize: 13, fontWeight: 700, fontFamily: T.font,
            cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
          }}>View Digital ID</button>
        </div>
      )}

      {/* ── Records & Trust — 6 compact routable cards ── */}
      {D && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
          {[
            { title: 'Health Vault', subtitle: 'Records, documents & timeline', color: T.teal, icon: 'M12 1L2 5v8c0 7 4.3 11 10 13 5.7-2 10-6 10-13V5L12 1z', route: '/patient/health-vault' },
            { title: 'Timeline', subtitle: 'Your healthcare journey', color: T.navy, icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', route: '/patient/timeline' },
            { title: 'Lab Reports', subtitle: 'Results & tracking', color: '#7C3AED', icon: 'M22 12h-4l-3 9L9 3l-3 9H2', route: '/patient/lab-reports' },
            { title: 'Certificates', subtitle: 'Medical certificates', color: T.warning, icon: 'M12 15a4 4 0 100-8 4 4 0 000 8z M8 21l4-3 4 3 M9 11l-2 8 M15 11l2 8', route: '/patient/certificates' },
            { title: 'Consent Center', subtitle: 'Manage data sharing', color: T.success, icon: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z M9 12l2 2 4-4', route: '/patient/consent' },
            { title: 'Access History', subtitle: 'Who viewed your records', color: T.info, icon: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 9a3 3 0 100 6 3 3 0 000-6z', route: '/patient/access-logs' },
          ].map(r => (
            <div key={r.title} onClick={() => navigate(r.route)} style={{
              background: T.white, border: `1px solid ${T.border}`, borderRadius: 14,
              padding: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12,
              boxShadow: T.shadow,
            }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: `${r.color}0C`, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={r.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {r.icon.split(' M').map((seg, i) => <path key={i} d={i === 0 ? seg : `M${seg}`} />)}
                </svg>
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: T.font, lineHeight: 1.2 }}>{r.title}</div>
                <div style={{ fontSize: 10, color: T.textSec, fontFamily: T.font, marginTop: 1 }}>{r.subtitle}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── C. CARE SERVICES ── */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: D ? 16 : 10 }}>
          {sectionTitle('Care Services')}
          <span onClick={() => setShowAllServices(v => !v)} style={{ fontSize: D ? 13 : 9.5, fontWeight: 600, color: T.teal, fontFamily: T.font, cursor: 'pointer' }}>{showAllServices ? 'Show less' : 'View all services'}</span>
        </div>
        {D && sectionSub('Book doctors, telemedicine, labs, pharmacy, ambulance and insurance support')}
        <div style={{ display: 'grid', gridTemplateColumns: D ? '1fr 1fr 1fr' : '1fr 1fr', gap: D ? 16 : 8, marginTop: D ? 16 : 0 }}>
          {[
            { title: 'Book Doctor', desc: 'Find verified doctors nearby', action: 'View', accent: T.navy, icon: 'M12 11a4 4 0 100-8 4 4 0 000 8z M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2 M18 8v4 M16 10h4', route: '/patient/care-services/doctors' },
            { title: 'Telemedicine', desc: 'Video consult from home', action: 'Book', accent: T.info, icon: 'M23 7l-7 5 7 5V7z M1 5h15v14H1z', route: '/patient/care-services/telemedicine' },
            // The count is the server's own tally of lab bookings still in
            // flight — it was fetched with the dashboard and never shown.
            { title: 'Lab Test', desc: 'NABL labs, home collection', action: 'Book', accent: T.teal, icon: 'M9 3h6 M10 3v6.5L5.5 18a2 2 0 001.8 3h9.4a2 2 0 001.8-3L14 9.5V3', route: '/patient/care-services/labs', count: data?.stats.pendingLabCount ?? 0 },
            { title: 'Order Medicines', desc: 'Pharmacy delivery & eRx', action: 'Order', accent: '#059669', icon: 'M10.5 20.5 4 14a4.95 4.95 0 017-7l6.5 6.5a4.95 4.95 0 01-7 7Z M8.5 8.5l7 7', route: '/patient/care-services/pharmacy' },
            { title: 'Book Ambulance', desc: 'ALS/BLS emergency dispatch', action: 'SOS', accent: T.danger, icon: 'M3 7h11v8H3z M14 10h4l3 3v2h-7 M5 19a1.5 1.5 0 100-3 1.5 1.5 0 000 3z M17 19a1.5 1.5 0 100-3 1.5 1.5 0 000 3z M8.5 9v3 M7 10.5h3', route: '/patient/care-services/ambulance' },
            { title: 'Insurance / Claims', desc: 'Coverage & pre-auth', action: 'View', accent: T.warning, icon: 'M12 1L2 5v8c0 7 4.3 11 10 13 5.7-2 10-6 10-13V5L12 1z M9 12l2 2 4-4', route: '/patient/care-services/insurance' },
            { title: 'Clinic Visit', desc: 'Walk-in clinics near you', action: 'Book', accent: T.teal, extra: true, icon: 'M3 21h18 M5 21V7l8-4v18 M19 21V11l-6-3 M9 9v.01 M9 12v.01 M9 15v.01', route: '/patient/care-services/hospitals' },
            { title: 'Hospital OPD', desc: 'Outpatient departments', action: 'Book', accent: T.purple, extra: true, icon: 'M3 21h18 M6 21V5a2 2 0 012-2h8a2 2 0 012 2v16 M12 7v6 M9 10h6', route: '/patient/care-services/hospitals' },
            { title: 'Imaging / Diagnostics', desc: 'MRI, CT, X-Ray, Ultrasound', action: 'Book', accent: IMAGING_ACCENT, extra: true, icon: 'M12 3a9 9 0 100 18 9 9 0 000-18z M3 12h18 M6.3 6.3l11.4 11.4', route: '/patient/care-services/imaging' },
            { title: 'Health Vault', desc: 'Your health records', action: 'Open', accent: T.teal, extra: true, icon: 'M12 1L2 5v8c0 7 4.3 11 10 13 5.7-2 10-6 10-13V5L12 1z', route: '/patient/health-vault' },
            { title: 'Certificates', desc: 'Medical certificates', action: 'View', accent: T.navy, extra: true, icon: 'M12 15a4 4 0 100-8 4 4 0 000 8z M8 21l4-3 4 3 M9 11l-2 8 M15 11l2 8', route: '/patient/certificates' },
            { title: 'Payments', desc: 'Bills & invoices', action: 'Pay', accent: T.success, extra: true, icon: 'M2 7h20v12H2z M2 11h20 M6 15h4', route: '/patient/care-services' },
          ].filter((s) => showAllServices || !('extra' in s && s.extra)).map((s) => (
            <div key={s.title} onClick={() => navigate(s.route)} style={dCard({ display: 'flex', alignItems: D ? 'flex-start' : 'center', flexDirection: D ? 'column' : 'row', gap: D ? 12 : 8, cursor: 'pointer', minHeight: D ? 140 : undefined, overflow: 'hidden' as const })}>
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <div style={iconBox(s.accent, D ? 48 : 32)}>
                  <svg width={D ? 22 : 13} height={D ? 22 : 13} viewBox="0 0 24 24" fill="none" stroke={s.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {s.icon.split(' M').map((seg, i) => <path key={i} d={i === 0 ? seg : `M${seg}`} />)}
                  </svg>
                </div>
                {/* A live tally from the dashboard; zero renders nothing, since
                    an empty badge would be a claim of its own. */}
                {'count' in s && (s.count ?? 0) > 0 && (
                  <span style={{
                    position: 'absolute', top: -4, right: -4,
                    minWidth: D ? 18 : 15, height: D ? 18 : 15, padding: '0 4px',
                    display: 'grid', placeItems: 'center', boxSizing: 'border-box',
                    background: s.accent, color: '#FFF', borderRadius: 9,
                    border: `1.5px solid ${T.white}`,
                    fontSize: D ? 10 : 8.5, fontWeight: 700, lineHeight: 1, fontFamily: T.font,
                  }}>
                    {(s.count ?? 0) > 9 ? '9+' : s.count}
                  </span>
                )}
              </div>
              {D ? (
                <div style={{ flex: 1, width: '100%' }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: T.font, lineHeight: 1.2, marginBottom: 4 }}>{s.title}</div>
                  <div style={{ fontSize: 12, color: T.textSec, fontFamily: T.font, lineHeight: 1.4, marginBottom: 12 }}>{s.desc}</div>
                  <button style={{ ...dActionBtn(s.accent, '#FFF'), width: '100%', textAlign: 'center' as const }}>{s.action}</button>
                </div>
              ) : (
                <>
                  <div style={{ flex: 1, minWidth: 0, fontSize: 10, fontWeight: 700, color: T.text, fontFamily: T.font, lineHeight: 1.15 }}>{s.title}</div>
                  <button style={{ ...actionBtn(s.accent, '#FFF'), fontSize: 8.5, padding: '5px 9px', flexShrink: 0 }}>{s.action}</button>
                </>
              )}
            </div>
          ))}
        </div>
        {D && (
          <div onClick={() => navigate('/patient/care-services')} style={{
            marginTop: 16, textAlign: 'center', cursor: 'pointer',
            padding: '12px 0', borderRadius: 12, border: `1px solid ${T.teal}20`,
            background: `${T.teal}06`,
          }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.teal, fontFamily: T.font }}>
              Open Care Services →
            </span>
          </div>
        )}
      </div>

      {/* ── TIMELINE PREVIEW ── */}
      {D && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            {sectionTitle('Timeline')}
            <span onClick={() => navigate('/patient/timeline')} style={{ fontSize: 13, fontWeight: 600, color: T.teal, fontFamily: T.font, cursor: 'pointer' }}>View full timeline →</span>
          </div>
          {sectionSub('Your recent healthcare events')}
          <div style={{
            background: T.white, border: `1px solid ${T.border}`, borderRadius: 14,
            padding: 20, boxShadow: T.shadow, marginTop: 12,
          }}>
            {/* The server's own merged activity (`recentTimeline` on the
                dashboard). This used to render the browser-local store, so it
                showed different events from the audit strip below it — and
                nothing at all on a device the patient had not used before. */}
            {recentTimeline.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, position: 'relative', paddingLeft: 18 }}>
                <div style={{ position: 'absolute', left: 5, top: 4, bottom: 4, width: 2, background: T.border, borderRadius: 1 }} />
                {recentTimeline.slice(0, 4).map(event => {
                  const colors: Record<string, string> = { APPOINTMENT: T.teal, LAB: T.info, PRESCRIPTION: T.success, DOCUMENT: T.info, ACCOUNT: T.textSec, PHARMACY: '#059669', EMERGENCY: T.danger };
                  const c = colors[(event.eventType || '').toUpperCase()] || T.textSec;
                  const diff = Date.now() - new Date(event.eventDate).getTime();
                  const hrs = Math.floor(diff / 3600000);
                  const timeStr = hrs < 1 ? 'Just now' : hrs < 24 ? `${hrs}h ago` : hrs < 48 ? 'Yesterday' : `${Math.floor(hrs / 24)}d ago`;
                  return (
                    <div key={event.id} style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{
                        position: 'absolute', left: -16, top: 3, width: 10, height: 10, borderRadius: 5,
                        background: `${c}20`, border: `2px solid ${c}`, zIndex: 1,
                      }} />
                      <div style={{ flex: 1, marginLeft: 4 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.text, fontFamily: T.font, lineHeight: 1.3 }}>{event.title}</div>
                      </div>
                      <span style={{ fontSize: 11, color: T.textTer, fontFamily: T.font, flexShrink: 0, whiteSpace: 'nowrap' }}>{timeStr}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '12px 0', fontSize: 12, color: T.textSec, fontFamily: T.font }}>
                No recent activity yet — your healthcare events will appear here.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── RECENT ACTIVITY / AUDIT STREAM (Desktop — Task 6) ── */}
      {D && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            {sectionTitle('Recent Activity')}
            <span onClick={() => navigate('/patient/access-logs')} style={{ fontSize: 13, fontWeight: 600, color: T.teal, fontFamily: T.font, cursor: 'pointer' }}>View all logs →</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {AUDIT_ENTRIES.length > 0 ? (
              AUDIT_ENTRIES.slice(0, 4).map((entry, i) => (
                <div key={i} style={{
                  background: T.white, border: `1px solid ${T.border}`, borderRadius: 14,
                  padding: 16, display: 'flex', alignItems: 'center', gap: 14, boxShadow: T.shadow,
                }}>
                  <div style={{ width: 8, height: 8, borderRadius: 4, background: entry.color, flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: T.text, fontFamily: T.font }}>{entry.text}</span>
                  <span style={{ fontSize: 11, color: T.textTer, fontFamily: T.font, flexShrink: 0 }}>{entry.time}</span>
                </div>
              ))
            ) : (
              <div style={{
                background: T.white, border: `1px solid ${T.border}`, borderRadius: 14,
                padding: 16, textAlign: 'center', fontSize: 12, color: T.textSec, fontFamily: T.font,
              }}>
                No activity logged yet.
              </div>
            )}
          </div>
          <div style={{ fontSize: 11, color: T.textTer, fontFamily: T.font, textAlign: 'center', marginTop: 12 }}>
            Consent-based access is logged · All activity is auditable
          </div>
        </div>
      )}

      {/* ── B. IMPORTANT ALERTS — unread notifications from the real backend ── */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: D ? 16 : 8 }}>
          {sectionTitle('Important Alerts')}
          {alerts.length > 2 && (
            <span onClick={() => setShowAllAlerts(v => !v)} style={{ fontSize: D ? 13 : 9.5, fontWeight: 600, color: T.teal, fontFamily: T.font, cursor: 'pointer' }}>{showAllAlerts ? 'Show less' : 'View all'}</span>
          )}
        </div>
        {alerts.length === 0 ? (
          <div style={dCard({ textAlign: 'center' as const, padding: D ? 20 : 14 })}>
            <span style={{ fontSize: D ? 13 : 10, color: T.textSec, fontFamily: T.font }}>No alerts right now — you're all caught up.</span>
          </div>
        ) : (
          alerts
            .filter((_, i) => showAllAlerts || i < 2)
            .map((a, i, arr) => {
              const accent = a.category === 'EMERGENCY' ? T.danger : a.category === 'MEDICINE' ? T.warning : T.info;
              return (
                <div key={a.id} onClick={() => navigate('/patient/notifications')} style={dCard({ marginBottom: i < arr.length - 1 ? (D ? 12 : 8) : 0, display: 'flex', alignItems: 'center', gap: D ? 16 : 10, cursor: 'pointer' })}>
                  <div style={iconBox(accent, D ? 44 : 36)}><AlertDot c={accent} /></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: D ? 14 : 10, fontWeight: 600, color: T.text, fontFamily: T.font, lineHeight: 1.3 }}>{a.title}</div>
                    {a.body && <div style={{ fontSize: D ? 11 : 8, color: T.textSec, fontFamily: T.font, marginTop: D ? 4 : 2 }}>{a.body}</div>}
                    <div style={{ fontSize: D ? 11 : 8, color: T.textTer, fontFamily: T.font, marginTop: 2 }}>{formatDate(a.createdAt)}</div>
                  </div>
                  <span style={{ ...dPill(a.category, accent), flexShrink: 0 }}>{a.category}</span>
                </div>
              );
            })
        )}
      </div>

      {/* ── E. MY HEALTH — NHCTitaniumCard + shortcuts ── */}
      <div>
        <div style={{ marginBottom: D ? 16 : 8 }}>{sectionTitle('My Health')}</div>
        {pid && (
          <div onClick={() => navigate('/patient/nhc-card')} style={{ cursor: 'pointer', marginBottom: D ? 14 : 9, display: 'flex', justifyContent: 'center' }}>
            <ApprovedPatientCard
              name={pid.patientName}
              nhcId={pid.nhcIdDisplay}
              nhcIdRaw={pid.nhcIdRaw}
              bloodGroup={pid.bloodGroup}
              insuranceStatus={pid.insuranceStatus}
            />
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: D ? 'repeat(4, 1fr)' : 'repeat(4, 1fr)', gap: D ? 16 : 6 }}>
          {[
            { label: 'Health Vault', color: T.teal, icon: 'M12 1L2 5v8c0 7 4.3 11 10 13 5.7-2 10-6 10-13V5L12 1z', route: '/patient/health-vault' },
            { label: 'Timeline', color: T.info, icon: 'M12 7v5l3 2 M12 3a9 9 0 100 18 9 9 0 000-18z', route: '/patient/timeline' },
            { label: 'Certificates', color: T.navy, icon: 'M12 15a4 4 0 100-8 4 4 0 000 8z M8 21l4-3 4 3 M9 11l-2 8 M15 11l2 8', route: '/patient/certificates' },
            { label: 'Digital ID', color: T.purple, icon: 'M3 3h7v7H3z M14 3h7v7h-7z M3 14h7v7H3z M14 14h3v3h-3z M14 21h3 M21 14v3 M21 21h.01', route: '/patient/nhc-card' },
          ].map((m) => (
            <div key={m.label} onClick={() => navigate(m.route)} style={dCard({ textAlign: 'center' as const, cursor: 'pointer', padding: D ? 14 : 8, overflow: 'hidden' as const })}>
              <div style={{ ...iconBox(m.color, D ? 48 : 30), margin: `0 auto ${D ? 10 : 5}px` }}>
                <svg width={D ? 20 : 12} height={D ? 20 : 12} viewBox="0 0 24 24" fill="none" stroke={m.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {m.icon.split(' M').map((seg, i) => <path key={i} d={i === 0 ? seg : `M${seg}`} />)}
                </svg>
              </div>
              <div style={{ fontSize: D ? 13 : 8, fontWeight: 700, color: T.text, fontFamily: T.font, lineHeight: 1.2 }}>{m.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── G. VERIFIED PROVIDERS — real NeoVerified directory listings ── */}
      {nearbyProviders.length > 0 && (
        <div>
          <div style={{ marginBottom: D ? 4 : 2 }}>{sectionTitle('Verified Providers')}</div>
          {sectionSub('NeoVerified providers on the NeoHealthCard network')}
          <div style={{ marginTop: D ? 16 : 10, ...(D ? { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 } as React.CSSProperties : { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 } as React.CSSProperties) }}>
            {nearbyProviders.map((p) => {
              const meta = PROVIDER_TYPE_META[p.type] ?? PROVIDER_TYPE_META.DOCTOR;
              return (
                <div key={p.id} onClick={() => navigate(meta.route)} style={dCard({ overflow: 'hidden' as const, cursor: 'pointer' })}>
                  <div style={{ ...iconBox(meta.color, D ? 44 : 32), marginBottom: D ? 10 : 6 }}>
                    <span style={{ fontSize: D ? 13 : 9, fontWeight: 700, color: meta.color, fontFamily: T.font }}>{p.name.split(' ').map(w => w[0]).slice(0, 2).join('')}</span>
                  </div>
                  <div style={{ fontSize: D ? 14 : 10, fontWeight: 700, color: T.text, fontFamily: T.font, marginBottom: D ? 4 : 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{p.name}</div>
                  <div style={{ fontSize: D ? 12 : 8, color: T.textTer, fontFamily: T.font, marginBottom: D ? 8 : 4 }}>{[p.specialties?.[0], p.city].filter(Boolean).join(' · ') || meta.label}</div>
                  <div style={{ marginBottom: D ? 12 : 6 }}>
                    <span style={{ ...dPill(meta.label, meta.color), fontSize: D ? 11 : 7.5 }}>{meta.label}</span>
                  </div>
                  <button style={{ ...dActionBtn(meta.color, '#FFF'), width: '100%', textAlign: 'center' as const, fontSize: D ? 13 : 9, padding: D ? '10px 20px' : '6px 8px' }}>{meta.btn}</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── AUDIT TRAIL ── */}
      {AUDIT_ENTRIES.length > 0 && (
        <div onClick={() => navigate('/patient/timeline')} style={{ cursor: 'pointer' }}>
          <NHCAuditTimeline entries={AUDIT_ENTRIES} title="Recent Activity" />
        </div>
      )}
    </div>
  );
}
